import { PropsWithChildren, useMemo } from "react";

import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import Paper from "@material-ui/core/Paper";

import { QUERIES } from "../../../graphql";
import { Amenity } from "../../../graphql/models";
import { ApartmentDataService } from "../../../graphql/models/ApartmentDataService";
import { useSnackbarQuery } from "../../../hooks";
import useApartmentServicesEditorStyles from "./apartment_services_editor.style";

export interface ApartmentServicesEditorProps extends PropsWithChildren<any> {
  buildingId: string;
  apartmentServices: ApartmentDataService[];
  apartmentServicesChange: (value: ApartmentDataService[]) => void;
}

export function ApartmentServicesEditor(props: ApartmentServicesEditorProps) {
  const { buildingId, apartmentServices, apartmentServicesChange } = props;

  const classes = useApartmentServicesEditorStyles();

  const { data } = useSnackbarQuery(QUERIES.AMENITIES, {
    fetchPolicy: "no-cache",
    variables: {
      buildingId,
    },
  });

  const allServices: Amenity[] = useMemo(() => {
    if (data) {
      return data.amenities;
    }

    return [];
  }, [data]);

  const viewServices = useMemo(() => {
    const viewServicesArr: ApartmentDataService[] = [];
    for (const service of allServices) {
      const existingRecord = apartmentServices.find(
        (v) => v.service?.id === service.id
      );
      viewServicesArr.push({
        service: existingRecord?.service ?? {
          id: service.id,
          name: service.name,
        },
        maxUnits: existingRecord?.maxUnits ?? 0,
      });
    }

    return viewServicesArr;
  }, [apartmentServices, allServices]);

  const setMaxUnits = (row: ApartmentDataService, maxUnits: number) => {
    const apartmentServicesCopy = [...apartmentServices];
    const itemIndex = apartmentServicesCopy.findIndex(
      (v) => v.service?.id === row.service.id
    );

    if (itemIndex !== -1) {
      apartmentServicesCopy[itemIndex] = {
        ...apartmentServicesCopy[itemIndex],
        maxUnits,
      };
    } else {
      apartmentServicesCopy.push({
        service: { ...row.service },
        maxUnits,
      });
    }

    apartmentServicesChange(apartmentServicesCopy);
  };

  return (
    <TableContainer component={Paper}>
      <Table className={classes.table} aria-label="elenco servizi">
        <caption>
          Questi campi definiscono il numero massimo di persone prenotabili per
          servizio, per prenotazione (per l'appartamento in oggetto).
          <br />
          Se non viene indicato nessun valore oppure viene indicato 0, il
          servizio della riga non potrà essere prenotato.
        </caption>
        <TableHead>
          <TableRow>
            <TableCell>Servizio</TableCell>
            <TableCell align="right">Numero massimo di persone</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {viewServices.map((row: ApartmentDataService) => (
            <TableRow key={row.service.id}>
              <TableCell component="th" scope="row">
                {row.service.name}
              </TableCell>
              <TableCell align="right">
                <TextField
                  className={classes.textField}
                  label="Numero massimo di persone"
                  variant="outlined"
                  type="number"
                  value={row.maxUnits}
                  onChange={(e) => setMaxUnits(row, Number(e.target.value))}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
